// apiFetch: einheitlicher Fetch-Helper mit RFC-7807-Error-Parsing,
// Trace-ID-Header, Auth-Cookie, JSON-Default. Generic, tenant-agnostisch.

import { traceManager } from "../utils/trace-manager.js";
import { dispatchSessionExpiredBanner } from "./banner-bus.js";
import { readSourceSync } from "./context-store.js";

export interface ApiErrorBody {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  instance?: string;
  code?: string;
  trace_id?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody | string;
  detail?: string;
  code?: string;
  traceId?: string;

  constructor(status: number, body: ApiErrorBody | string, fallback: string) {
    const detail = typeof body === "object" && body.detail ? body.detail : undefined;
    const message = detail ?? fallback;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.detail = detail;
    this.code = typeof body === "object" ? body.code : undefined;
    this.traceId = typeof body === "object" ? body.trace_id : undefined;
  }
}

export interface ApiFetchOptions extends RequestInit {
  // forwardTraceId: setzt X-TRACE-ID-Header (default true).
  forwardTraceId?: boolean;
}

/**
 * apiBase ermittelt die API-Base-URL generisch über die Host-Schnittstelle
 * (`readSourceSync("apiBase")`) — KEIN `__PFS_API_BASE__`-Global im Framework.
 * Der Host (mednet-main bzw. der WP-Embed-Adapter) provided den Wert; das
 * Framework selbst kennt keine umgebungsspezifische Quelle. Kein Provider
 * (echtes Standalone) → `""` → Calls laufen relativ auf der aktuellen Origin.
 *
 * Geteilt, damit Konsumenten (Cut-Apps) ihre Proxy-URLs nicht jeweils selbst
 * aus dem Context zusammenbauen müssen.
 */
export function apiBase(): string {
  return readSourceSync("apiBase");
}

// withApiBase — EIN zentraler, idempotenter Mechanismus für ALLE Components +
// Cut-Apps (Pascal 2026-07-12: „den gleichen Mechanismus über alle hinweg"):
// relative /-Pfade bekommen den Host-Mount-Prefix (mednet-main, z.B.
// /mitgliederbereich-app) ZUR FETCH-ZEIT vorangestellt — nie beim Render
// eingefroren (der Freeze-Bug: WCs lasen apiBase() beim Upgrade, BEVOR der Host
// die Source in main() bereitstellte → "" → URL ohne Prefix → 404).
// Idempotent: schon-geprefixte URLs (beginnen mit apiBase()) + absolute URLs
// (http…) bleiben unverändert; apiBase()="" (Standalone) → no-op. So funktioniert
// sowohl `apiFetch("/api/proxy/…")` (bare) als auch das alte `apiBase()+url`.
export function withApiBase(url: string): string {
  if (!url.startsWith("/")) return url;
  const base = apiBase();
  if (!base) return url;
  return url === base || url.startsWith(base + "/") ? url : base + url;
}

/**
 * SessionVerifier prüft, ob die Session real noch lebt. `true` = lebt,
 * `false` = tot. Wird (optional) per `setSessionVerifier` registriert.
 */
export type SessionVerifier = () => Promise<boolean>;

let sessionVerifier: SessionVerifier | null = null;
let inflightProbe: Promise<boolean> | null = null;

/**
 * setSessionVerifier registriert einen App-spezifischen Session-Probe.
 *
 * PFS-467: Ein einzelner 401 beweist NICHT, dass die Session abgelaufen
 * ist — er kann endpoint-spezifisch sein (fehlende Domain-Row, fehlende
 * Permission, transienter Race). Ohne Verifier behält apiFetch das
 * Legacy-Verhalten (Banner bei jedem 401). Mit Verifier eskaliert apiFetch
 * nur dann zum „Sitzung abgelaufen"-Banner + Auto-Logout, wenn der Probe
 * bestätigt, dass die Session tot ist. Auto-Logout ist destruktiv → nie
 * ohne Verifikation.
 *
 * Der Probe sollte NUR die Session-Liveness prüfen (z.B. mednet-main
 * `/auth/session`: valkey-Session + Token-Gültigkeit), NICHT einen
 * Cut-App-spezifischen Endpoint — sonst maskiert dieselbe Ursache wie der
 * ursprüngliche 401 die Prüfung.
 *
 * `null` setzt zurück aufs Legacy-Verhalten.
 */
export function setSessionVerifier(fn: SessionVerifier | null): void {
  sessionVerifier = fn;
}

/**
 * installAggregatorSessionVerifier registriert den kanonischen Session-
 * Probe für PFS-Shell- + Cut-App-Bundles (PFS-467). Eine Stelle, von
 * mednet-main UND allen Cut-Apps genutzt — kein dupliziertes Closure pro
 * Bundle.
 *
 * Alle Browser-API-Calls der Cut-Apps laufen über `${apiBase}/api/proxy/…`
 * (den Aggregator/mednet-main); dieselbe Authority beantwortet
 * `${apiBase}/auth/session` (valkey-Session-Liveness, KEINE Cut-App-Row).
 * `apiBase` fragt das Framework generisch beim Host ab (`readSourceSync(
 * "apiBase")`): mednet-main (bzw. WP-Embed-Adapter) provided ihn — das
 * Framework selbst kennt KEINE umgebungsspezifische Quelle. Kein Provider
 * (echtes Standalone) → "" → Probe läuft auf der Aggregator-Origin.
 *
 * Liveness-Semantik (fail-safe Richtung „User drin lassen"):
 *   - Probe 200            → Session lebt → kein Logout.
 *   - Probe 401            → Session definitiv tot → Banner + Auto-Logout.
 *   - Probe 404/5xx/Fehler → inconclusive (z.B. echtes Standalone ohne
 *     Aggregator-Probe, transienter Netzwerkfehler) → KEIN Zwangs-Logout.
 * Damit braucht es keine zweite Authority (pfs-auth) — ein Kontext ohne
 * `/auth/session` führt nie zu einem fälschlichen Logout.
 */
export function installAggregatorSessionVerifier(): void {
  setSessionVerifier(async () => {
    // apiBase über die generische Host-Schnittstelle (kein __PFS_API_BASE__-
    // Global im Framework). "apiBase" = lokales Literal, matcht den
    // @mednet/context-Katalog by-convention.
    let res: Response;
    try {
      res = await fetch(withApiBase("/auth/session"), {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
    } catch {
      return true; // Netzwerkfehler → inconclusive → User drin lassen
    }
    // Nur ein definitives 401 heißt „Session tot". Alles andere
    // (200 lebt, 404/5xx Probe-nicht-verfügbar) → drin lassen.
    return res.status !== 401;
  });
}

/**
 * isSessionDead korreliert einen 401 gegen den registrierten Verifier.
 * Parallele 401-Wellen teilen sich EINEN Probe (in-flight-Dedupe), damit
 * nicht jeder fehlgeschlagene Call einen eigenen Probe-Request auslöst.
 */
function isSessionDead(): Promise<boolean> {
  const verifier = sessionVerifier;
  // Kein Verifier → Legacy: 401 wird als Session-Tod behandelt.
  if (!verifier) return Promise.resolve(true);
  if (!inflightProbe) {
    inflightProbe = verifier()
      .then((alive) => !alive)
      // Probe selbst gescheitert (z.B. Netzwerk) → inconclusive. Fail-safe
      // Richtung „User drin lassen": ein transienter Probe-Fehler darf
      // keinen Zwangs-Logout auslösen.
      .catch(() => false)
      .finally(() => {
        inflightProbe = null;
      });
  }
  return inflightProbe;
}

/**
 * escalateIfSessionDead — bei einem 401 aufrufen, wenn der Fetch NICHT durch
 * apiFetch lief (z.B. der pfs-shell-Nav-Partial-Fetch, der ein HTML-Fragment
 * statt JSON holt und deshalb `fetch` direkt nutzt). Korreliert den 401 gegen
 * den registrierten Verifier (PFS-467, in-flight-dedupe) und zeigt NUR bei real
 * toter Session den „Sitzung abgelaufen"-Banner + Auto-Logout — ein einzelner
 * endpoint-spezifischer 401 reißt die Session nicht ab. Idempotent/best-effort.
 */
export async function escalateIfSessionDead(): Promise<void> {
  if (await isSessionDead()) {
    dispatchSessionExpiredBanner();
  }
}

export async function apiFetch<T>(url: string, opts: ApiFetchOptions = {}): Promise<T> {
  url = withApiBase(url); // zentraler Fetch-Zeit-Prefix (idempotent, s.o.)
  const headers = new Headers(opts.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (opts.forwardTraceId !== false) {
    headers.set("X-TRACE-ID", traceManager.getTraceId());
  }

  const response = await fetch(url, {
    credentials: "include",
    ...opts,
    headers,
  });

  if (response.status === 401) {
    // PFS-430/467: nur bei real toter Session zum „Sitzung abgelaufen"-Banner +
    // Auto-Logout eskalieren (Verifier-Probe; einzelner 401 reißt nicht ab).
    await escalateIfSessionDead();
    throw new ApiError(401, "", "unauthenticated");
  }

  // 204 No Content (leerer Erfolg, z.B. DELETE/Revoke): kein JSON-Body
  // erwartbar → nicht als Fehler behandeln. Ohne das würde jede 204-Antwort
  // unten am application/json-Guard fälschlich werfen (Revoke „fehlgeschlagen").
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    if (!response.ok) {
      throw new ApiError(response.status, await response.text(), `HTTP ${response.status}`);
    }
    throw new ApiError(response.status, "", "expected application/json");
  }

  const body = (await response.json()) as ApiErrorBody | T;

  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody, `HTTP ${response.status}`);
  }

  return body as T;
}
